/**
 * Force-logout flag watcher.
 *
 * The foreground-service notification has a "Logout to disable" action.
 * When tapped, Android delivers the intent to MainActivity and IpChangePlugin
 * writes a `force_logout_pending` flag to SharedPreferences. Capacitor event
 * delivery through notifyListeners during the onNewIntent → onResume window
 * on singleTask activities is unreliable, so we do not depend on it.
 *
 * This module polls the flag on every resume (and once at startup). When
 * the flag is set it runs the full logout flow via the caller-provided
 * callback, which is expected to reset the Pinia auth store and navigate
 * the user back to the Login route.
 */

import { isAndroid } from "./platform";

let _watcherInstalled = false;

/**
 * Reads and clears the native "force logout pending" flag. Returns true if
 * a logout was requested (and has now been consumed); false otherwise or on
 * any error. Safe to call on web (no-op, returns false).
 */
export async function consumeForceLogoutFlag(): Promise<boolean> {
  if (!isAndroid()) return false;
  try {
    const { default: IpChange } = await import("./ip-change-plugin");
    const { pending } = await IpChange.consumeForceLogoutFlag();
    return pending;
  } catch {
    return false;
  }
}

/**
 * Installs a single appStateChange listener that consumes the flag on every
 * resume and an immediate check on install to cover cold start after the
 * user tapped the notification while the process was dead. Safe to call
 * multiple times; only the first call takes effect. No-op on web.
 */
export async function initForceLogoutWatcher(onForceLogout: () => Promise<void>): Promise<void> {
  if (!isAndroid() || _watcherInstalled) return;
  _watcherInstalled = true;

  if (await consumeForceLogoutFlag()) {
    await onForceLogout();
  }

  const { App } = await import("@capacitor/app");
  await App.addListener("appStateChange", async (state) => {
    if (state.isActive && (await consumeForceLogoutFlag())) {
      await onForceLogout();
    }
  });
}
